// Finance > Invoicing (AR) — the invoicing WORK QUEUE (handoff W25 / W14).
//
// One list, filtered into the jobs finance actually does:
//   Ready to invoice — shipped quotes with no invoice yet
//                      (GET /orders?stage=shipped; converting happens on the
//                      quote, so each row opens it)
//   Not sent         — invoices raised but not sent        (status=open)
//   Sent             — sent / exported, awaiting payment   (status=sent_to_client|exported)
//   Past due         — open invoices past their DUE date: last delivery +
//                      the client's terms (server buckets 1-30 / 31-60 / 61-90 / 90+)
//   Part-paid        — something received, a balance still owed (server
//                      `outstanding` > 0 and below the invoice total)
//   Paid / All
//
// Every money figure is the SERVER's: `outstanding` is balance_due minus the
// ledgered payments (floored at 0). This page compares and renders those
// strings — it never adds them up. The summary strip reads the Finance
// dashboard rollup (server totals) for the same reason.
//
// URL params `status` and `bucket` still pre-filter on landing so the Finance
// dashboard links keep working (e.g. /finance/invoicing?bucket=90%2B).
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { PageHeader } from "@/components/ui/PageHeader";
import {
  FilterTabs,
  MetricStrip,
  TABLE_HEAD,
  TABLE_WRAP,
  TD,
  TH,
  TROW,
} from "@/components/ui/RecordParts";
import { StatusPill } from "@/components/ui/StatusPill";
import { QboInvoiceExportCard } from "@/components/finance/QboInvoiceExportCard";
import { cn } from "@/lib/utils";

type InvoiceStatus =
  | "open"
  | "sent_to_client"
  | "exported"
  | "paid"
  | "voided";
// Aged by DUE date — last delivery + the client's terms (Stephanie 2026-10-16).
// `current` = not due yet; the rest are days PAST due.
type AgingBucket = "current" | "1-30" | "31-60" | "61-90" | "90+" | "paid" | "voided";

interface InvoiceRow {
  id: string;
  invoiceNumber: number;
  orderId: string;
  orderNumber: number;
  projectId: string;
  projectName: string;
  clientId: string;
  clientName: string;
  invoiceType: "deposit" | "final";
  invoiceDate: string;
  status: InvoiceStatus;
  total: string;
  balanceDue: string | null;
  /** Server-computed: balance_due minus the ledgered payments, floored at 0.
   *  balance_due nets DEPOSITS only, so a part-paid invoice would otherwise
   *  show its full value in the AR grid. */
  outstanding: string;
  bucket: AgingBucket;
  /** YYYY-MM-DD; null = waiting on delivery (the due clock hasn't started). */
  dueDate?: string | null;
  paidAt: string | null;
  createdAt: string;
}

interface ListResponse {
  rows: InvoiceRow[];
  nextCursor: { createdAt: string; id: string } | null;
}

interface ShippedOrderRow {
  id: string;
  orderNumber: number;
  projectId: string;
  clientId: string;
  clientName: string;
  statusStage: string;
  statusState: string;
  customerPo: string | null;
  inHandsDate: string;
  createdAt: string;
  updatedAt: string;
}

interface OrdersResponse {
  rows: ShippedOrderRow[];
  nextCursor: { createdAt: string; id: string } | null;
}

interface BucketCell {
  count: number;
  amount: string;
}
interface DashboardResponse {
  arSummary: {
    totalOpenCount: number;
    totalOpenAmount: string;
    current: BucketCell;
    past1to30: BucketCell;
    past31to60: BucketCell;
    past61to90: BucketCell;
    pastOver90: BucketCell;
  };
  fetchedAt: string;
}

type View = "ready" | "open" | "sent" | "overdue" | "partial" | "paid" | "all";

const VIEWS: ReadonlyArray<{ value: View; label: string }> = [
  { value: "ready", label: "Ready to invoice" },
  { value: "open", label: "Not sent" },
  { value: "sent", label: "Sent" },
  { value: "overdue", label: "Past due" },
  { value: "partial", label: "Part-paid" },
  { value: "paid", label: "Paid" },
  { value: "all", label: "All" },
];

type AgeFilter = "any" | "current" | "1-30" | "31-60" | "61-90" | "90+";

const OVERDUE_BUCKETS: AgingBucket[] = ["1-30", "31-60", "61-90", "90+"];
const isPastDueBucket = (b: string | null): b is "1-30" | "31-60" | "61-90" | "90+" =>
  b === "1-30" || b === "31-60" || b === "61-90" || b === "90+";
const OPEN_STATUSES: InvoiceStatus[] = ["open", "sent_to_client", "exported"];

function statusLabel(s: InvoiceStatus): string {
  switch (s) {
    case "open":
      return "Not sent";
    case "sent_to_client":
      return "Sent";
    case "exported":
      return "Exported";
    case "paid":
      return "Paid";
    case "voided":
      return "Voided";
  }
}

function statusTone(s: InvoiceStatus): "active" | "info" | "dormant" | "closed" {
  switch (s) {
    case "open":
      return "info";
    case "sent_to_client":
    case "exported":
      return "info";
    case "paid":
      return "active";
    case "voided":
      return "closed";
  }
}

function ageLabel(b: AgingBucket): string {
  switch (b) {
    case "current":
      return "Not due yet";
    case "1-30":
      return "1–30 days past due";
    case "31-60":
      return "31–60 days past due";
    case "61-90":
      return "61–90 days past due";
    case "90+":
      return "90+ days past due";
    case "paid":
      return "Paid";
    case "voided":
      return "Voided";
  }
}

function formatCurrency(amount: string | null): string {
  if (amount === null) return "—";
  const n = Number(amount);
  if (Number.isNaN(n)) return amount;
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function formatDate(s: string | null): string {
  if (!s) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return new Date(s + "T00:00:00").toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** Part-paid = something received, a balance still owed. Both sides are the
 *  server's own strings — a comparison, not a calculation. */
function isPartPaid(r: InvoiceRow): boolean {
  if (r.status === "paid" || r.status === "voided") return false;
  const out = Number(r.outstanding);
  const total = Number(r.total);
  return Number.isFinite(out) && Number.isFinite(total) && out > 0 && out < total;
}

/** Landing state from the URL (?status= / ?bucket=) — dashboard deep links. */
function initialFromUrl(
  status: string | null,
  bucket: string | null,
  viewParam: string | null,
): { view: View; age: AgeFilter } {
  if (isPastDueBucket(bucket)) return { view: "overdue", age: bucket };
  if (bucket === "current") return { view: "all", age: "current" };
  switch (status) {
    case "open":
      return { view: "open", age: "any" };
    case "sent_to_client":
    case "exported":
      return { view: "sent", age: "any" };
    case "paid":
      return { view: "paid", age: "any" };
    case "voided":
      return { view: "all", age: "any" };
  }
  if (status || bucket) return { view: "all", age: "any" };
  // `?view=` is written by this page for the tabs the dashboard doesn't link.
  if (viewParam === "part-paid") return { view: "partial", age: "any" };
  if (viewParam === "past-due" || viewParam === "30-plus") return { view: "overdue", age: "any" };
  if (viewParam === "all") return { view: "all", age: "any" };
  return { view: "ready", age: "any" };
}

export function InvoicingPage() {
  const { user } = useAuth();
  // QuickBooks export is finance/admin work (the endpoints are gated the same).
  const canManage = user?.roles.some((r) => r === "admin" || r === "finance") ?? false;
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const urlStatus = search.get("status");
  const urlBucket = search.get("bucket");
  const initial = initialFromUrl(urlStatus, urlBucket, search.get("view"));

  const [view, setViewState] = useState<View>(initial.view);
  const [age, setAgeState] = useState<AgeFilter>(initial.age);

  // Keep the address bar in step so a filtered view can be shared/bookmarked
  // with the same ?status= / ?bucket= params the dashboard uses.
  function syncUrl(v: View, a: AgeFilter) {
    const next = new URLSearchParams();
    if (v === "open") next.set("status", "open");
    if (v === "sent") next.set("status", "sent_to_client");
    if (v === "paid") next.set("status", "paid");
    if ((v === "overdue" || v === "all") && a !== "any") next.set("bucket", a);
    if (v === "ready") next.set("view", "ready");
    if (v === "partial") next.set("view", "part-paid");
    if (v === "overdue" && a === "any") next.set("view", "past-due");
    if (v === "all" && a === "any") next.set("view", "all");
    setSearch(next, { replace: true });
  }
  function setView(v: View) {
    const a: AgeFilter = "any";
    setViewState(v);
    setAgeState(a);
    syncUrl(v, a);
  }
  function setAge(a: AgeFilter) {
    setAgeState(a);
    syncUrl(view, a);
  }

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (view === "open") params.append("status", "open");
    if (view === "sent") {
      params.append("status", "sent_to_client");
      params.append("status", "exported");
    }
    if (view === "paid") params.append("status", "paid");
    if (view === "partial") for (const s of OPEN_STATUSES) params.append("status", s);
    if (view === "overdue") {
      if (isPastDueBucket(age)) params.append("bucket", age);
      else for (const b of OVERDUE_BUCKETS) params.append("bucket", b);
    }
    if (view === "all" && age !== "any") params.append("bucket", age);
    params.set("limit", "100");
    return params.toString();
  }, [view, age]);

  const invoicesQ = useQuery({
    queryKey: ["finance", "invoices", queryString],
    queryFn: () => api<ListResponse>(`/invoices?${queryString}`),
    enabled: view !== "ready",
  });

  // Shipped quotes = shipped, not yet converted to an invoice (converting
  // moves the order to `invoiced`). Fetched always: it feeds the strip too.
  const readyQ = useQuery({
    queryKey: ["finance", "orders", "shipped"],
    queryFn: () => api<OrdersResponse>("/orders?stage=shipped&limit=100"),
  });

  const dashQ = useQuery({
    queryKey: ["finance", "dashboard"],
    queryFn: () => api<DashboardResponse>("/finance/dashboard"),
  });

  const allRows = invoicesQ.data?.rows ?? [];
  const rows = view === "partial" ? allRows.filter(isPartPaid) : allRows;
  const readyRows = readyQ.data?.rows ?? [];
  const ar = dashQ.data?.arSummary;
  const agedCount = ar ? ar.past1to30.count + ar.past31to60.count + ar.past61to90.count + ar.pastOver90.count : null;

  const ageOptions: ReadonlyArray<{ value: AgeFilter; label: string }> =
    view === "overdue"
      ? [
          { value: "any", label: "All past due" },
          { value: "1-30", label: "1–30 days" },
          { value: "31-60", label: "31–60 days" },
          { value: "61-90", label: "61–90 days" },
          { value: "90+", label: "90+ days" },
        ]
      : [
          { value: "any", label: "Any age" },
          { value: "current", label: "Not due yet" },
          { value: "1-30", label: "1–30 past due" },
          { value: "31-60", label: "31–60 past due" },
          { value: "61-90", label: "61–90 past due" },
          { value: "90+", label: "90+ past due" },
        ];

  const emptyCopy: Record<View, [string, string]> = {
    ready: ["Nothing waiting to be invoiced.", "Shipped quotes show up here until they're converted to an invoice."],
    open: ["No unsent invoices.", "Invoices raised from a quote wait here until someone sends them."],
    sent: ["No sent invoices awaiting payment.", "Sent invoices stay here until they're paid or voided."],
    overdue: ["Nothing past due.", "Open invoices land here once they pass their due date — the last delivery plus the client's terms."],
    partial: ["No part-paid invoices.", "An invoice shows here when a payment is recorded but a balance is still owed."],
    paid: ["No paid invoices yet.", "Invoices move here once payments clear the balance."],
    all: ["No invoices match.", "Choose another age, or another tab."],
  };

  return (
    <div className="mx-auto w-full">
      <PageHeader
        eyebrow="Finance / Invoicing"
        title="Invoicing"
        intro="Shipped orders to invoice, invoices to send, and balances to collect. Balances come from the payment ledger."
        action={
          canManage && (
            <Link
              to="/finance/quickbooks?entity=invoices"
              className="inline-flex h-10 items-center gap-2 rounded-md border border-rule bg-white px-[14px] text-sm font-medium text-ink hover:bg-canvas"
            >
              <Upload className="h-4 w-4" /> Export to QuickBooks
            </Link>
          )
        }
      />

      {/* Invoices not in QuickBooks yet, ready to send in one click (Stephanie 2026-11-03). */}
      {canManage && <QboInvoiceExportCard />}

      <div className="mb-7">
        <MetricStrip
          ariaLabel="Invoicing summary"
          cells={[
            {
              label: "Ready to invoice",
              value: readyQ.isError ? "—" : readyQ.data ? readyRows.length : null,
              note: readyQ.data?.nextCursor ? "Shipped orders · first 100" : "Shipped, not invoiced",
              onClick: () => setView("ready"),
              attention: readyRows.length > 0,
            },
            {
              label: "Accounts receivable",
              value: dashQ.isError ? "—" : ar ? formatCurrency(ar.totalOpenAmount) : null,
              note: ar ? `${ar.totalOpenCount.toLocaleString()} open ${ar.totalOpenCount === 1 ? "invoice" : "invoices"}` : undefined,
              onClick: () => setView("all"),
            },
            {
              label: "Past due",
              value: dashQ.isError ? "—" : agedCount,
              note: "Open invoices · count",
              onClick: () => setView("overdue"),
              attention: (agedCount ?? 0) > 0,
            },
            {
              label: "Not due yet",
              value: dashQ.isError ? "—" : ar ? formatCurrency(ar.current.amount) : null,
              note: ar ? `${ar.current.count.toLocaleString()} ${ar.current.count === 1 ? "invoice" : "invoices"}` : undefined,
            },
          ]}
        />
        {dashQ.data && (
          <p className="mt-2 text-xs text-muted">
            Open balances as of {new Date(dashQ.data.fetchedAt).toLocaleString()} · age counts from the due date (last delivery + terms).
          </p>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-end justify-between gap-x-6 gap-y-3 border-b border-rule">
        <FilterTabs ariaLabel="Invoice work queue" options={VIEWS} value={view} onChange={setView} />
        {(view === "overdue" || view === "all") && (
          <div
            className="mb-2 inline-flex h-10 flex-wrap rounded-md border border-rule bg-white p-0.5"
            role="group"
            aria-label="Invoice age"
          >
            {ageOptions.map((o) => (
              <button
                key={o.value}
                type="button"
                aria-pressed={age === o.value}
                onClick={() => setAge(o.value)}
                className={cn(
                  "rounded px-3 text-sm transition-colors",
                  age === o.value ? "bg-ink font-medium text-white" : "text-muted hover:text-ink",
                )}
              >
                {o.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {view === "ready" ? (
        <>
          <div className={TABLE_WRAP} role="region" aria-label="Shipped orders ready to invoice" tabIndex={0}>
            {readyQ.isLoading ? (
              <LoadingRows label="Loading shipped orders" />
            ) : readyQ.isError ? (
              <div className="p-5 text-sm text-risk" role="alert">
                We couldn't load shipped orders. Nothing has changed — refresh to try again.
              </div>
            ) : readyRows.length === 0 ? (
              <Empty title={emptyCopy.ready[0]} detail={emptyCopy.ready[1]} />
            ) : (
              <table className="w-full text-left">
                <thead className={TABLE_HEAD}>
                  <tr>
                    <th scope="col" className={TH}>Quote</th>
                    <th scope="col" className={`${TH} hidden md:table-cell`}>Client</th>
                    <th scope="col" className={`${TH} hidden sm:table-cell`}>In hands</th>
                    <th scope="col" className={`${TH} hidden lg:table-cell`}>Last updated</th>
                    <th scope="col" className={`${TH} text-right`}>
                      <span className="sr-only">Action</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="text-[13px]">
                  {readyRows.map((o) => (
                    <tr key={o.id} className={TROW}>
                      <td className={TD}>
                        <Link
                          to={`/projects/${o.projectId}/quotes/${o.id}`}
                          className="font-medium text-ink hover:underline"
                        >
                          Quote #{o.orderNumber}
                        </Link>
                        <span className="block text-[11px] text-muted">
                          <StatusPill tone="active" className="mr-1.5 align-middle normal-case">Shipped</StatusPill>
                          {o.customerPo ? `PO ${o.customerPo}` : ""}
                          <span className="md:hidden">{o.customerPo ? " · " : ""}{o.clientName}</span>
                        </span>
                      </td>
                      <td className={`${TD} hidden text-ink md:table-cell`}>{o.clientName}</td>
                      <td className={`${TD} hidden text-ink sm:table-cell`}>{formatDate(o.inHandsDate)}</td>
                      <td className={`${TD} hidden text-muted lg:table-cell`}>{formatDate(o.updatedAt)}</td>
                      <td className={`${TD} text-right`}>
                        <Link
                          to={`/projects/${o.projectId}/quotes/${o.id}`}
                          className="inline-flex min-h-[40px] items-center whitespace-nowrap rounded-md border border-rule bg-white px-3.5 text-sm text-ink hover:bg-canvas"
                        >
                          Open to invoice
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <p className="mt-3 text-xs text-muted">
            Invoices are created on the quote (Convert to invoice) — nothing here changes an order.
            {readyQ.data?.nextCursor && " Showing the latest 100 shipped orders."}
          </p>
        </>
      ) : (
        <>
          <div className={TABLE_WRAP} role="region" aria-label="Invoices" tabIndex={0}>
            {invoicesQ.isLoading ? (
              <LoadingRows label="Loading invoices" />
            ) : invoicesQ.isError ? (
              <div className="p-5 text-sm text-risk" role="alert">
                We couldn't load invoices. Nothing has changed — refresh to try again.
              </div>
            ) : rows.length === 0 ? (
              <Empty title={emptyCopy[view][0]} detail={emptyCopy[view][1]} />
            ) : (
              <table className="w-full text-left">
                <thead className={TABLE_HEAD}>
                  <tr>
                    <th scope="col" className={TH}>Invoice</th>
                    <th scope="col" className={`${TH} hidden md:table-cell`}>Client / project</th>
                    <th scope="col" className={TH}>Status</th>
                    <th scope="col" className={`${TH} hidden lg:table-cell`}>Due</th>
                    <th scope="col" className={`${TH} hidden text-right sm:table-cell`}>Total</th>
                    <th scope="col" className={`${TH} text-right`}>Balance due</th>
                  </tr>
                </thead>
                <tbody className="text-[13px]">
                  {rows.map((r) => {
                    const partPaid = isPartPaid(r);
                    const aged = isPastDueBucket(r.bucket);
                    return (
                      <tr
                        key={r.id}
                        onClick={() => navigate(`/projects/${r.projectId}/invoice/${r.id}`)}
                        className={cn(TROW, "cursor-pointer")}
                      >
                        <td className={TD}>
                          <Link
                            to={`/projects/${r.projectId}/invoice/${r.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="font-medium text-ink hover:underline"
                          >
                            Invoice #{r.invoiceNumber}
                          </Link>
                          <span className="block text-[11px] text-muted">
                            {r.invoiceType === "deposit" ? "Prepay · " : ""}
                            Order #{r.orderNumber} · {formatDate(r.invoiceDate)}
                            <span className="md:hidden"> · {r.clientName}</span>
                          </span>
                        </td>
                        <td className={`${TD} hidden md:table-cell`}>
                          <span className="block text-ink">{r.clientName}</span>
                          <span className="block text-[11px] text-muted">{r.projectName}</span>
                        </td>
                        <td className={TD}>
                          <div className="flex flex-wrap gap-1.5">
                            <StatusPill className="normal-case" tone={statusTone(r.status)}>{statusLabel(r.status)}</StatusPill>
                            {partPaid && <StatusPill className="normal-case" tone="warn">Part-paid</StatusPill>}
                          </div>
                        </td>
                        <td className={cn(TD, "hidden lg:table-cell", aged ? "text-warn" : "text-muted")}>
                          {r.status === "paid" || r.status === "voided" ? (
                            ageLabel(r.bucket)
                          ) : (
                            <>
                              <span className="block text-ink">{r.dueDate ? formatDate(r.dueDate) : "Waiting on delivery"}</span>
                              <span className="block text-[11px]">{ageLabel(r.bucket)}</span>
                            </>
                          )}
                        </td>
                        <td className={`${TD} hidden text-right tabular-nums text-muted sm:table-cell`}>
                          {formatCurrency(r.total)}
                        </td>
                        <td className={`${TD} text-right font-medium tabular-nums text-ink`}>
                          {formatCurrency(r.outstanding ?? r.balanceDue ?? r.total)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <p className="mt-3 text-xs text-muted">
            Showing {rows.length} {rows.length === 1 ? "invoice" : "invoices"}
            {view === "partial" && " · checked against the 100 most recent open invoices"}
            {invoicesQ.data?.nextCursor && " · more available — narrow by age to find the rest"}
          </p>
        </>
      )}
    </div>
  );
}

function LoadingRows({ label }: { label: string }) {
  return (
    <div className="space-y-3 p-5" aria-label={label}>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-4 w-full animate-pulse rounded bg-canvas" />
      ))}
    </div>
  );
}

function Empty({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="px-5 py-12 text-center">
      <p className="font-medium text-ink">{title}</p>
      <p className="mt-1 text-sm text-muted">{detail}</p>
    </div>
  );
}
